import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRight, Check, CreditCard, ShieldCheck, Lock } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import Seo from "@/components/Seo";
import { api, fmtMoney, todayISO, addDaysISO } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { useDynamicLogo } from "@/lib/useLogo";
import PaymentBadges from "@/components/site/PaymentBadges";
import { toast } from "sonner";

export default function BookingPage() {
  const { roomId } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t, trField } = useI18n();
  const logoUrl = useDynamicLogo();
  const STEPS = [t("book.step1"), t("book.step2"), t("book.step3")];
  const [room, setRoom] = useState(null);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [redirecting, setRedirecting] = useState(null); // { url, amount_charged, currency_charged }

  const [form, setForm] = useState({
    check_in: params.get("check_in") || todayISO(),
    check_out: params.get("check_out") || addDaysISO(todayISO(), 2),
    guests: Number(params.get("guests") || 2),
    guest_name: user?.name || "",
    guest_email: user?.email || "",
    guest_phone: "",
    address_line: "",
    city: "",
    postal_code: "",
    country: "",
    special_requests: "",
  });

  useEffect(() => {
    api.get(`/rooms/${roomId}`).then((r) => setRoom(r.data)).catch(() => nav("/rooms"));
  }, [roomId, nav]);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, guest_name: f.guest_name || user.name, guest_email: f.guest_email || user.email }));
  }, [user]);

  if (!room) return <SiteLayout><div className="py-24 text-center">Loading…</div></SiteLayout>;

  const nights = Math.max(Math.round((new Date(form.check_out) - new Date(form.check_in)) / (1000 * 60 * 60 * 24)), 0);
  const subtotal = nights * room.price_per_night;
  const taxes = subtotal * 0.1;
  const total = subtotal + taxes;

  async function payNow() {
    setSubmitting(true);
    try {
      const b = await api.post("/bookings", { room_id: roomId, ...form });
      const origin_url = window.location.origin;
      const c = await api.post("/checkout/session", { booking_id: b.data.id, origin_url });
      // Show pre-redirect splash for 2 seconds so guest sees branding + amount
      setRedirecting({
        url: c.data.url,
        amount_eur: c.data.amount_eur,
        amount_charged: c.data.amount_charged,
        currency_charged: c.data.currency_charged,
      });
      setTimeout(() => { window.location.href = c.data.url; }, 1800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setSubmitting(false);
    }
  }

  if (redirecting) {
    return (
      <div data-testid="payment-redirect-splash" className="fixed inset-0 z-[100] bg-brand-bg flex flex-col items-center justify-center px-4">
        <img src={logoUrl} alt="Berdhill Rooms" className="h-20 md:h-24 mb-12 animate-fade-up" />
        <div className="text-center max-w-md">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-brand-muted">
            <Lock size={11} /> Secure checkout
          </div>
          <h1 className="font-display text-3xl md:text-4xl mt-3">Redirecting to SumUp…</h1>
          <p className="mt-4 text-brand-muted text-sm">
            Hold on while we securely transfer you to our payment partner.
          </p>
          <div className="mt-10 inline-flex items-center justify-center w-16 h-16 rounded-full border-2 border-brand-primary/20 border-t-brand-primary animate-spin" />
          <div className="mt-12 bg-white border border-brand-border rounded-md p-5 text-left text-sm">
            <div className="flex justify-between items-baseline">
              <span className="text-brand-muted">Total</span>
              <span className="font-display text-2xl text-brand-primary">{fmtMoney(redirecting.amount_eur)}</span>
            </div>
            {redirecting.currency_charged && redirecting.currency_charged !== "EUR" && (
              <div className="mt-1 text-xs text-brand-muted flex justify-between">
                <span>Charged in {redirecting.currency_charged}</span>
                <span className="font-mono">{Number(redirecting.amount_charged).toFixed(2)} {redirecting.currency_charged}</span>
              </div>
            )}
            <div className="mt-3 pt-3 border-t border-brand-border text-xs text-brand-muted">
              {trField(room, "name")} · {form.check_in} → {form.check_out} · {nights} night{nights !== 1 ? "s" : ""}
            </div>
            <div className="mt-4 pt-4 border-t border-brand-border flex items-center justify-center">
              <PaymentBadges size="sm" />
            </div>
          </div>
          <p className="mt-6 text-[11px] text-brand-muted">
            If you're not redirected automatically, <a href={redirecting.url} className="text-brand-primary underline">click here</a>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <SiteLayout>
      <Seo title={`${t("book.book_title")} ${trField(room, "name")} · Berdhill Rooms`} noindex />
      <div className="max-w-5xl mx-auto px-4 md:px-8 lg:px-12 pt-12">
        <div className="text-xs tracking-[0.2em] uppercase text-brand-muted">{t("book.reservation")}</div>
        <h1 className="font-display text-4xl md:text-5xl mt-3">{t("book.book_title")} {trField(room, "name")}</h1>

        <div className="mt-10 flex items-center gap-4">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-3">
              <div data-testid={`step-${i}`} className={`w-7 h-7 rounded-full flex items-center justify-center text-xs ${i <= step ? "bg-brand-primary text-white" : "bg-brand-secondary text-brand-muted"}`}>
                {i < step ? <Check size={14} /> : i + 1}
              </div>
              <span className={`text-sm ${i === step ? "font-medium" : "text-brand-muted"}`}>{s}</span>
              {i < STEPS.length - 1 && <ChevronRight size={14} className="text-brand-muted" />}
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-8 lg:px-12 mt-12 grid lg:grid-cols-3 gap-10 pb-24">
        <div className="lg:col-span-2 bg-white border border-brand-border rounded-sm p-6 md:p-10">
          {step === 0 && (
            <Step1
              form={form}
              setForm={setForm}
              room={room}
              onNext={() => setStep(1)}
            />
          )}
          {step === 1 && (
            <Step2
              form={form}
              setForm={setForm}
              onBack={() => setStep(0)}
              onNext={() => setStep(2)}
            />
          )}
          {step === 2 && (
            <Step3
              form={form}
              room={room}
              nights={nights}
              subtotal={subtotal}
              taxes={taxes}
              total={total}
              submitting={submitting}
              onBack={() => setStep(1)}
              onPay={payNow}
            />
          )}
        </div>

        <aside>
          <div className="lg:sticky lg:top-28 bg-white border border-brand-border rounded-sm p-6">
            <div className="aspect-[4/3] overflow-hidden rounded-sm">
              <img src={room.photos[0]} alt={trField(room, "name")} className="w-full h-full object-cover" />
            </div>
            <h3 className="font-display text-xl mt-4">{trField(room, "name")}</h3>
            <p className="text-xs text-brand-muted mt-1">{nights > 0 ? `${nights} night${nights > 1 ? "s" : ""}` : "Pick your dates"}</p>
            {nights > 0 && (
              <div className="mt-6 text-sm space-y-2">
                <Row label={`${fmtMoney(room.price_per_night)} × ${nights}`} value={fmtMoney(subtotal)} />
                <Row label="Taxes & fees" value={fmtMoney(taxes)} />
                <hr className="my-3 border-brand-border" />
                <Row label={<span className="font-medium">Total (EUR)</span>} value={<span className="font-medium text-brand-primary">{fmtMoney(total)}</span>} />
              </div>
            )}
          </div>
        </aside>
      </div>
    </SiteLayout>
  );
}

function Step1({ form, setForm, room, onNext }) {
  return (
    <div className="space-y-6">
      <h2 className="font-display text-2xl">When will you stay?</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Check-in"><input data-testid="book-checkin" type="date" min={todayISO()} value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value, check_out: form.check_out <= e.target.value ? addDaysISO(e.target.value, 1) : form.check_out })} className="berdhill-input" /></Field>
        <Field label="Check-out"><input data-testid="book-checkout" type="date" min={addDaysISO(form.check_in, 1)} value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} className="berdhill-input" /></Field>
      </div>
      <Field label="Guests">
        <select data-testid="book-guests" value={form.guests} onChange={(e) => setForm({ ...form, guests: Number(e.target.value) })} className="berdhill-input">
          {Array.from({ length: room.max_guests }).map((_, i) => (
            <option key={i} value={i + 1}>{i + 1} guest{i + 1 > 1 ? "s" : ""}</option>
          ))}
        </select>
      </Field>
      <button data-testid="step1-continue" onClick={onNext} className="bg-brand-primary hover:bg-brand-primaryHover text-white px-6 py-3 rounded-sm text-sm">Continue</button>
      <style>{`.berdhill-input{width:100%;border:1px solid #E6E2DB;border-radius:.125rem;padding:.625rem .75rem;font-size:.875rem;background:#fff}`}</style>
    </div>
  );
}

function Step2({ form, setForm, onBack, onNext }) {
  const ok = form.guest_name && form.guest_email;
  return (
    <div className="space-y-6">
      <h2 className="font-display text-2xl">Your details</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full name"><input data-testid="book-name" value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} className="berdhill-input" /></Field>
        <Field label="Email"><input data-testid="book-email" type="email" value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} className="berdhill-input" /></Field>
      </div>
      <Field label="Phone (optional)"><input data-testid="book-phone" value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })} className="berdhill-input" /></Field>

      <div className="pt-2 border-t border-brand-border" />
      <h3 className="font-display text-lg">Billing address <span className="text-xs text-brand-muted ml-2">(for your invoice)</span></h3>
      <Field label="Street address (optional)"><input data-testid="book-address" value={form.address_line} onChange={(e) => setForm({ ...form, address_line: e.target.value })} className="berdhill-input" /></Field>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="City"><input data-testid="book-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="berdhill-input" /></Field>
        <Field label="Postal code"><input data-testid="book-postal" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="berdhill-input" /></Field>
        <Field label="Country"><input data-testid="book-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="berdhill-input" /></Field>
      </div>

      <Field label="Special requests (optional)"><textarea data-testid="book-requests" rows={3} value={form.special_requests} onChange={(e) => setForm({ ...form, special_requests: e.target.value })} className="berdhill-input" /></Field>
      <div className="flex justify-between pt-2">
        <button onClick={onBack} className="text-sm text-brand-muted hover:text-brand-text" data-testid="step2-back">← Back</button>
        <button disabled={!ok} data-testid="step2-continue" onClick={onNext} className="bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-50 text-white px-6 py-3 rounded-sm text-sm">Continue</button>
      </div>
      <style>{`.berdhill-input{width:100%;border:1px solid #E6E2DB;border-radius:.125rem;padding:.625rem .75rem;font-size:.875rem;background:#fff}`}</style>
    </div>
  );
}

function Step3({ form, room, nights, subtotal, taxes, total, submitting, onBack, onPay }) {
  return (
    <div className="space-y-6">
      <h2 className="font-display text-2xl">Review & pay</h2>
      <div className="space-y-3 text-sm">
        <Row label="Room" value={room.name} />
        <Row label="Dates" value={`${form.check_in} → ${form.check_out}`} />
        <Row label="Guests" value={`${form.guests}`} />
        <Row label="Name" value={form.guest_name} />
        <Row label="Email" value={form.guest_email} />
      </div>
      <hr className="border-brand-border" />
      <div className="space-y-2 text-sm">
        <Row label={`${fmtMoney(room.price_per_night)} × ${nights} nights`} value={fmtMoney(subtotal)} />
        <Row label="Taxes & fees" value={fmtMoney(taxes)} />
        <Row label={<span className="font-medium">Total (EUR)</span>} value={<span className="font-medium text-brand-primary">{fmtMoney(total)}</span>} />
      </div>

      <div className="rounded-sm bg-brand-secondary/50 p-5 text-sm">
        <div className="flex items-start gap-3">
          <ShieldCheck className="text-brand-primary shrink-0 mt-0.5" size={18} />
          <div>
            <div className="font-medium">Secure payment</div>
            <p className="text-brand-muted mt-1">You will be redirected to our SumUp checkout to complete the payment. Tap to pay with Apple Pay or Google Pay, or use any major card.</p>
          </div>
        </div>
        <div className="mt-4 pl-7">
          <PaymentBadges size="sm" />
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <button onClick={onBack} className="text-sm text-brand-muted hover:text-brand-text" data-testid="step3-back">← Back</button>
        <button disabled={submitting} data-testid="pay-now-btn" onClick={onPay} className="bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-50 text-white px-7 py-3.5 rounded-sm text-sm flex items-center gap-2">
          <CreditCard size={16} /> {submitting ? "Redirecting…" : `Pay ${fmtMoney(total)}`}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.2em] text-brand-muted">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4"><span className="text-brand-muted">{label}</span><span className="text-right">{value}</span></div>
  );
}
